import { fetchTagListing } from "@platformx/authoring-apis";
import { buildTagSmEntry, parseTagSmEntries } from "./tagSm";

const LOOKUP_ROWS = 50;

// One in-flight/finished lookup per (category, name) for the lifetime of the page, so a tag is
// only ever looked up once however many times tag_sm is reconciled.
const valueCache = new Map<string, Promise<string>>();

const lookupTagValue = (category: string, name: string): Promise<string> => {
  const key = `${category}\u0000${name}`;
  const cached = valueCache.get(key);
  if (cached) return cached;
  const pending = fetchTagListing({
    searchCategory: category,
    searchString: name,
    start: 0,
    rows: LOOKUP_ROWS,
  })
    .then((res: any) => {
      const records = Array.isArray(res?.authoring_getTagsList) ? res.authoring_getTagsList : [];
      const match = records.find((record: any) => record?.tag_name === name);
      return match?.tag_value || name;
    })
    .catch(() => {
      // Don't cache a failure - let a later reconcile retry.
      valueCache.delete(key);
      return name;
    });
  valueCache.set(key, pending);
  return pending;
};

// tag_sm entries are sometimes derived from a tag NAME alone (default Content Type tag, legacy
// content with no tag_sm entry, ...), which can only fall back to value = name. For every such
// entry (a category is known and value still equals name), look up the tag's real tag_value via
// the tag listing and return the tag_sm with it filled in. Entries that already carry a distinct
// value, or whose lookup finds nothing, are returned unchanged.
export const resolveTagSmValues = (tagSm: unknown): Promise<string[]> =>
  Promise.all(
    parseTagSmEntries(tagSm).map(async (entry) => {
      const value =
        entry.category && entry.value === entry.name
          ? await lookupTagValue(entry.category, entry.name)
          : entry.value;
      return buildTagSmEntry(entry.category, entry.name, value);
    }),
  );
